import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import {
  completeOnboarding as completeOnboardingApi,
  fetchMyProfile,
  removeCert as removeCertApi,
  updateMyProfile,
  uploadCert as uploadCertApi,
  uploadEligibilityDoc as uploadEligibilityDocApi,
  uploadPhoto as uploadPhotoApi,
  type UploadCertPayload,
} from "@/features/workers/api/workersApi";
import type { UpdateWorkerProfilePayload, WorkerProfile } from "@/features/workers/types";

const QUERY_KEY = ["worker", "me"];

/** Invalidate every queryKey on the venue + ops side that embeds this
 *  worker's profile data (name, photo, certs, legal status, ID doc, etc.).
 *  Worker-side mutations are local to the worker tab, but if a venue or ops
 *  user later opens their queue, they need the fresh data — not whatever
 *  was cached from the previous fetch. See `frontend.md` rule #13. */
function invalidateCrossActorWorkerViews(qc: QueryClient): void {
  // Venue: applicant lists (every shift's `["applicants", shiftId]`) and
  // favourites — both embed worker name / photo / certs / city / rating.
  qc.invalidateQueries({ queryKey: ["applicants"] });
  qc.invalidateQueries({ queryKey: ["favourites", "mine"] });
  // Ops: pending approval queue (legal status, SIN, ID doc, certs) and the
  // all-workers admin list (status, photo, name).
  qc.invalidateQueries({ queryKey: ["ops", "pending-workers"] });
  qc.invalidateQueries({ queryKey: ["ops", "workers"] });
}

export function useWorkerProfile() {
  return useQuery<WorkerProfile>({ queryKey: QUERY_KEY, queryFn: fetchMyProfile });
}

export function useUpdateWorkerProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateWorkerProfilePayload) => updateMyProfile(payload),
    onSuccess: (data) => {
      qc.setQueryData(QUERY_KEY, data);
      // min_hourly_rate floors the worker's shift feed server-side, so any
      // profile save must blow away cached shift lists to pick up the new
      // floor on the next render.
      qc.invalidateQueries({ queryKey: ["shifts"] });
      invalidateCrossActorWorkerViews(qc);
    },
  });
}

export function useUploadPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadPhotoApi(file),
    onSuccess: (data) => {
      qc.setQueryData(QUERY_KEY, data);
      invalidateCrossActorWorkerViews(qc);
    },
  });
}

export function useUploadEligibilityDoc() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadEligibilityDocApi(file),
    onSuccess: (data) => {
      qc.setQueryData(QUERY_KEY, data);
      invalidateCrossActorWorkerViews(qc);
    },
  });
}

export function useUploadCert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UploadCertPayload) => uploadCertApi(payload),
    onSuccess: (data) => {
      qc.setQueryData(QUERY_KEY, data);
      invalidateCrossActorWorkerViews(qc);
    },
  });
}

export function useRemoveCert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (certId: string) => removeCertApi(certId),
    onSuccess: (data) => {
      qc.setQueryData(QUERY_KEY, data);
      invalidateCrossActorWorkerViews(qc);
    },
  });
}

export function useCompleteOnboarding() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => completeOnboardingApi(),
    onSuccess: (data) => {
      qc.setQueryData(QUERY_KEY, data);
      // Onboarding submission flips the worker into the ops pending queue,
      // so this is the most important moment to flush cross-actor caches.
      invalidateCrossActorWorkerViews(qc);
    },
  });
}
